import useMute from 'hooks/useMute'
import useBlock from 'hooks/useBlock'
import CommonDialog from 'components/modals/CommonDialog'
import ConfirmModal from 'components/modals/ConfirmModal'
import { FC, useState } from 'react'
import { cn, useIsMobile } from '@wellesley/ui'
import { useRelationship } from '@wellesley/user'
import { useGetApiV1GroupsByGroupIdRulesQuery } from '@wellesley/openapi'

import { REPORT_STEP } from '../ReportList/options'
import { NOT_FAN_ACTION } from '../NotFanReport/options'
import { MOBILE_BOTTOM_DIALOG_CLASSNAME } from '../../mobile/MobileBottomModal'

import ModalContent from './ModalContent'
import { DEFAULT_TITLE, INIT_OPTION, TReportModal } from './types'

const getReportTitle = (option: string) =>
  (({
    not_fan: 'What do you want to do?',
    server_rules: `Which rules have been broken`,
    group_rules: `Which rules have been broken`,
  })[option] as string) || DEFAULT_TITLE

const ReportModal: FC<TReportModal> = ({
  isOpen,
  onClose,
  groupId,
  removePosts,
  ...props
}) => {
  const isMobile = useIsMobile()
  const [isSuccess, setIsSuccess] = useState(false)

  const [notFunAction, setNotFunAction] = useState<NOT_FAN_ACTION>('block')
  const [selectedOption, setSelectedOption] = useState<
    REPORT_STEP | typeof INIT_OPTION
  >(INIT_OPTION)

  const modalTitle = getReportTitle(selectedOption)

  const { data: groupRules } = useGetApiV1GroupsByGroupIdRulesQuery(
    { groupId: groupId as string },
    { skip: !groupId || !isOpen },
  )

  const { relationship, isLoading: isRelationsLoading } = useRelationship({
    userId: props.user.id,
    skip: !isOpen,
  })

  const { mute: handleMute, isLoading: isMuteLoading } = useMute({
    user: props.user,
    relationship,
    showConfirmationDialog: false,
    onMute: onClose,
  })

  const [, handleBlock] = useBlock({
    user: props.user,
    relationship,
    showConfirmationDialog: false,
    onBlocked: onClose,
  })

  const handleClose = () => {
    setIsSuccess(false)
    onClose()
  }

  const handleBack =
    selectedOption !== INIT_OPTION
      ? () => setSelectedOption(INIT_OPTION)
      : undefined

  if (isRelationsLoading || isMuteLoading) return null

  if (isSuccess)
    return (
      <ConfirmModal
        open
        onClose={handleClose}
        title="Report sent"
        leftButton={{
          size: 'm',
          variant: 'primary',
          isLoading: false,
          title: 'Okay, thanks',
          onClick: () => {
            handleClose()
            setIsSuccess(false)
            if (selectedOption === 'not_fan' && notFunAction === 'mute') {
              removePosts(props.user.id)
            }
          },
        }}
      >
        Thank you for submission. Report has been received and will be reviewed
        shortly
      </ConfirmModal>
    )

  return (
    <CommonDialog
      open={isOpen}
      title={modalTitle}
      className={cn(
        'p-none overflow-auto',
        isMobile && MOBILE_BOTTOM_DIALOG_CLASSNAME,
      )}
      childrenClassName="!p-0"
      onCloseModal={handleClose}
      onBack={handleBack}
    >
      <ModalContent
        onMute={handleMute}
        onBlock={handleBlock}
        isOpen={isOpen}
        onClose={handleClose}
        showSuccess={() => {
          setIsSuccess(true)
          setSelectedOption(INIT_OPTION)
        }}
        groupRules={groupRules || null}
        selectedOption={selectedOption}
        onSelectedOption={setSelectedOption}
        notFunAction={notFunAction}
        setNotFunAction={setNotFunAction}
        {...props}
      />
    </CommonDialog>
  )
}

export default ReportModal
